import type { ApiClient } from '@carbonwebmail/api-client'
import type { SearchHitDto } from '@carbonwebmail/contracts'
import { Link } from 'react-router'
import { Icon } from './Icon.js'
import { useSearch } from './useSearch.js'

/**
 * Finding a thing by the name somebody remembers.
 *
 * A person with a campaign in mind knows its name, not which of six screens
 * lists it. Without this the alternative is opening each one and using its own
 * filter, which is the job a search bar in the shell exists to remove.
 *
 * The kind decides where a hit goes. The server sends what each thing *is* and
 * never a URL, because a server that named client routes would break the day
 * one is renamed — so the map below is the only place that knowledge lives.
 *
 * "Nothing matched" and "we could not ask" are different sentences. They look
 * identical if you collapse them, and only one of them means the thing is not
 * there.
 */

const ROUTES: Record<SearchHitDto['kind'], (id: string) => string> = {
  contact: (id) => `/contacts/${id}`,
  campaign: (id) => `/campaigns/${id}`,
  segment: (id) => `/segments/${id}`,
  // Templates have no screen of their own; the list is where one is opened.
  template: () => '/templates',
}

const KINDS: Record<SearchHitDto['kind'], string> = {
  contact: 'Contact',
  campaign: 'Campaign',
  segment: 'Segment',
  template: 'Template',
}

export function SearchBar({ client }: { readonly client: ApiClient }) {
  const { term, setTerm, state, clear } = useSearch({ client })

  return (
    <div className="relative w-full max-w-md">
      <span className="pointer-events-none absolute top-2.5 left-3 text-t3">
        <Icon name="search" />
      </span>
      <input
        type="search"
        value={term}
        aria-label="Search"
        placeholder="Find a contact, campaign, segment or template"
        onChange={(event) => setTerm(event.target.value)}
        className="w-full rounded-pill border border-line bg-surface py-2 pr-3 pl-9 text-[13px] text-t1"
      />

      {state.status === 'idle' ? null : (
        <div className="absolute top-11 right-0 left-0 z-10 overflow-hidden rounded-card border border-line bg-surface shadow-lg">
          {state.status === 'searching' ? (
            <p className="px-3 py-3 text-[13px] text-t3">Looking…</p>
          ) : null}

          {state.status === 'failed' ? (
            <p className="px-3 py-3 text-[13px] text-danger">
              We could not search just now. That is not the same as finding nothing — try again.
            </p>
          ) : null}

          {state.status === 'found' && state.hits.length === 0 ? (
            <p className="px-3 py-3 text-[13px] text-t3">Nothing matched “{term}”.</p>
          ) : null}

          {state.status === 'found'
            ? state.hits.map((hit) => (
                <Link
                  key={`${hit.kind}-${hit.id}`}
                  to={ROUTES[hit.kind](hit.id)}
                  onClick={clear}
                  className="flex items-baseline gap-2 border-line border-b px-3 py-2.5 last:border-b-0 hover:bg-surface-2"
                >
                  <span className="min-w-0 flex-1 truncate font-medium text-[13px] text-t1">
                    {hit.label}
                  </span>
                  {hit.detail === '' ? null : (
                    <span className="min-w-0 max-w-[45%] truncate text-[12px] text-t3">
                      {hit.detail}
                    </span>
                  )}
                  <span className="flex-none text-[11px] text-t3 uppercase">{KINDS[hit.kind]}</span>
                </Link>
              ))
            : null}
        </div>
      )}
    </div>
  )
}
